import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Card } from '@kss/ui';
import { encodeId } from '../utils/idCodec';
import { useNotifications } from './NotificationsContext';

/**
 * Dashboard widget — renders the last N in-memory notifications so
 * an admin who missed a toast can scroll back and open the order.
 *
 * Empty state doubles as a hint that the feed is live.
 */
export default function RecentNotificationsCard() {
  const { recent, hub } = useNotifications();
  const navigate = useNavigate();

  // Re-render every 30s so "2 min ago" advances even when no new
  // notifications arrive.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <Card>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-content-primary">
            Recent notifications
          </h3>
        </div>
        <span className="text-[11px] text-content-muted">
          {hub ? (recent.length ? `${recent.length} in this session` : 'Live') : 'Offline'}
        </span>
      </div>

      {recent.length === 0 ? (
        <div className="flex h-[220px] flex-col items-center justify-center gap-2 rounded border border-dashed border-border/70 p-4 text-center">
          <Bell className="h-6 w-6 text-content-muted" />
          <p className="text-xs text-content-muted">
            No new orders yet — this list updates in real time as
            customers checkout.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border/60">
          {recent.map((n) => {
            const p = n.payload;
            const amount = formatInr(p.totalAmount);
            const rel = relativeTime(n.receivedAt);
            return (
              <li
                key={n.id}
                className="flex items-start justify-between gap-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm font-medium text-content-primary">
                      {p.orderNumber || 'New order'}
                    </span>
                    <span className="text-xs text-content-muted">{rel}</span>
                  </div>
                  <div className="mt-0.5 truncate text-xs text-content-secondary">
                    {p.customerName || 'Customer'} · {amount}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    p.salesOrderId &&
                    navigate(`/panel/sales-orders/${encodeId(p.salesOrderId)}`)
                  }
                  disabled={!p.salesOrderId}
                  className="shrink-0 rounded border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Open
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function formatInr(amount: number | undefined): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/**
 * "2 min ago" style formatter. Kept dependency-free — the whole
 * app already avoids date-fns / dayjs.
 */
function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 45) return 'just now';
  if (secs < 90) return '1 min ago';
  const mins = Math.round(secs / 60);
  if (mins < 45) return `${mins} min ago`;
  if (mins < 90) return '1 hr ago';
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}
